import React, { useState, useEffect } from 'react';
import {
  Container, Typography, TextField, List, ListItemButton,
  ListItemText, CircularProgress, Alert, Button, Box, IconButton,
  Chip, FormControlLabel, Switch, ListItemAvatar
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiFetch } from '../apiFetch';
import { ShoppingList } from '../types/ShoppingList';
import ShoppingListFormModal from '../modals/ShoppingListFormModal';
import DeleteModal from '../modals/DeleteModal';
import { useTutorial } from '../hooks/useTutorial';

const ShoppingLists: React.FC = () => {
  const navigate = useNavigate();
  const [shoppingLists, setShoppingLists] = useState<ShoppingList[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCompleted, setShowCompleted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingList, setEditingList] = useState<ShoppingList | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingList, setDeletingList] = useState<ShoppingList | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useTutorial('shopping_lists', { hasData: shoppingLists.length > 0 });

  useEffect(() => {
    const fetchShoppingLists = async () => {
      try {
        const data = await apiFetch(`${apiUrl}/api/shopping-lists/`);
        setShoppingLists(data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching shopping lists.');
      } finally {
        setLoading(false);
      }
    };
    fetchShoppingLists();
  }, [apiUrl]);

  // Format date for display
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format secondary text for list item
  const formatSecondaryText = (list: ShoppingList): string => {
    const parts = [];

    // Add item count (use item_count_all to include deleted groceries)
    const itemCount = list.item_count_all ?? 0;
    parts.push(`${itemCount} item${itemCount !== 1 ? 's' : ''}`);

    if (list.created_at) parts.push(`Created: ${formatDate(list.created_at)}`);
    if (list.description) parts.push(list.description);
    return parts.join(' | ');
  };

  // Filter shopping lists based on search term and completed status
  const filteredLists = shoppingLists
    .filter((list) => {
      // Filter by completed status
      if (!showCompleted && list.completed) return false;

      const lowerSearch = searchTerm.toLowerCase();
      // Get store name (from shop_name for active/paused lists, or completed_at_shop_name for completed lists)
      const storeName = list.shop_name || list.completed_at_shop_name || '';
      return (
        list.name.toLowerCase().includes(lowerSearch) ||
        (list.description && list.description.toLowerCase().includes(lowerSearch)) ||
        storeName.toLowerCase().includes(lowerSearch)
      );
    })
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA;
    });

  // Count completed lists
  const completedCount = shoppingLists.filter((list) => list.completed).length;

  // Handle opening the Add modal
  const handleAddClick = () => {
    setEditingList(null);
    setFormModalOpen(true);
  };

  // Handle opening the Edit modal
  const handleEditClick = (e: React.MouseEvent, list: ShoppingList) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingList(list);
    setFormModalOpen(true);
  };

  // Handle opening the Delete modal
  const handleDeleteClick = (e: React.MouseEvent, list: ShoppingList) => {
    e.preventDefault();
    e.stopPropagation();
    setDeletingList(list);
    setDeleteModalOpen(true);
  };

  // Handle successful form submission (add or edit)
  const handleFormSuccess = (savedList: ShoppingList) => {
    if (editingList) {
      setShoppingLists((prev) =>
        prev.map((l) => (l.id === savedList.id ? savedList : l))
      );
      setSuccessMessage('Shopping list updated successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } else {
      // Navigate to the new list view page
      navigate(`/shopping-lists/${savedList.id}`);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deletingList) return;
    try {
      await apiFetch(`${apiUrl}/api/shopping-lists/${deletingList.id}/`, {
        method: 'DELETE',
      });
      setShoppingLists((prev) => prev.filter((l) => l.id !== deletingList.id));
      setSuccessMessage('Shopping list deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      setError(err.message || 'An error occurred while deleting the shopping list.');
    } finally {
      setDeleteModalOpen(false);
      setDeletingList(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row">
          <IconButton onClick={() => navigate('/')} aria-label="back to home">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">Manage Your Shopping Lists</Typography>
        </Box>
        <Box className="page-header-buttons">
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleAddClick} data-tutorial="lists-new-button">
            New List
          </Button>
        </Box>
      </Box>
      <Typography variant="body1" gutterBottom>
        Here you can create, review, and manage your shopping lists. Plan your shopping trips and keep track of your grocery needs.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search bar */}
      <TextField
        label="Search Shopping Lists"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        data-tutorial="lists-search"
      />

      {/* Show completed toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }} data-tutorial="lists-completed-toggle">
        <FormControlLabel
          control={
            <Switch
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
          }
          label={`Show completed (${completedCount})`}
        />
      </Box>

      {loading && <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <List sx={{ mt: 2 }} data-tutorial="lists-list">
        {filteredLists.map((list) => {
          const getStatusChip = () => {
            if (list.status === 'completed' || list.completed) {
              return <Chip label="Done" size="small" color="primary" variant="filled" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
            }
            if (list.status === 'active') {
              return <Chip label="Active" size="small" color="success" variant="filled" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
            }
            if (list.status === 'paused') {
              return <Chip label="Paused" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
            }
            return <Chip label="Open" size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: '20px', lineHeight: '18px', minWidth: 50 }} />;
          };

          // Get store name for display
          const storeName = list.shop_name || list.completed_at_shop_name;
          const displayName = storeName ? `${list.name} | ${storeName}` : list.name;

          return (
          <ListItemButton
            key={list.id}
            component={RouterLink}
            to={`/shopping-lists/${list.id}`}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <ListItemAvatar sx={{ alignSelf: 'flex-start', mt: 1, mr: 1 }}>
              {getStatusChip()}
            </ListItemAvatar>
            <ListItemText
              primary={displayName}
              secondary={formatSecondaryText(list)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={(e) => handleEditClick(e, list)}
                aria-label="edit shopping list"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, list)}
                aria-label="delete shopping list"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </ListItemButton>
          );
        })}
      </List>

      {!loading && shoppingLists.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No shopping lists yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the "New List" button above to create your first shopping list.
          </Typography>
        </Box>
      )}

      {!loading && shoppingLists.length > 0 && filteredLists.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {showCompleted ? 'No shopping lists match your search.' : 'No open shopping lists. Toggle "Show completed" to see completed lists.'}
          </Typography>
        </Box>
      )}

      {/* Add/Edit Shopping List Modal */}
      <ShoppingListFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        shoppingList={editingList}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingList(null);
        }}
        onDelete={handleDeleteConfirm}
        itemName={deletingList?.name || ''}
      />
    </Container>
  );
};

export default ShoppingLists;